import { FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { UseFormReturn } from "react-hook-form";
import { SignUpFormData } from "../types";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Eye, EyeOff } from "lucide-react";
import { useTranslation } from "react-i18next";



interface AccountStepProps {
  form: UseFormReturn<SignUpFormData>;
}

export const AccountStep = ({ form }: AccountStepProps) => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const { t } = useTranslation();


  const COUNTRIES = [
    { code: "DE", name: t("countries.de") },
    { code: "FR", name: t("countries.fr") },
    { code: "GB", name: t("countries.gb") },
    { code: "ES", name: t("countries.es") },
    { code: "IT", name: t("countries.it") },
    { code: "PT", name: t("countries.pt") },
    { code: "SI", name: t("countries.si") },
    { code: "US", name: t("countries.us") },
  ];


  return (
    <>
      {/* <div className="mb-6">
        <h2 className="text-2xl font-bold">Create your account</h2>
        <p className="text-gray-600 mt-2">Fill in your details to get started</p>
      </div> */}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <FormField
          control={form.control}
          name="business_type"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-[#8791F3]">{t("sign_up.business_type")}</FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger className="text-[#34375B] font-normal bg-white border border-gray-300 p-2 rounded-md">
                    <SelectValue placeholder="Select business type" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent className="bg-white border border-gray-300 p-2 rounded-md">
                  <SelectItem value="individual">{t("sign_up.individual")}</SelectItem>
                  <SelectItem value="company">{t("sign_up.company")}</SelectItem>
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="country"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-[#8791F3]">{t("sign_up.country")}</FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger className="text-[#34375B] font-normal bg-white border border-gray-300 p-2 rounded-md">
                    <SelectValue placeholder={t("sign_up.select_country")} />
                  </SelectTrigger>
                </FormControl>
                <SelectContent className="bg-white border border-gray-300 p-2 rounded-md">
                  {COUNTRIES.map((country) => (
                    <SelectItem key={country.code} value={country.code}>
                      {country.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      <FormField
        control={form.control}
        name="business_name"
        render={({ field }) => (
          <FormItem>
            <FormLabel className="text-[#8791F3]">{t("seller_info.business_name")}</FormLabel>
            <FormControl>
              <Input className="bg-white border border-gray-300 p-2 rounded-md" placeholder={t("seller_info.business_name_placeholder")} {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="email"
        render={({ field }) => (
          <FormItem>
            <FormLabel className="text-[#8791F3]">{t("seller_info.email")}</FormLabel>
            <FormControl>
              <Input type="email" className="bg-white border border-gray-300 p-2 rounded-md" placeholder={t("seller_info.email_placeholder")} {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="password"
        render={({ field }) => (
          <FormItem>
            <FormLabel className="text-[#8791F3]">{t("auth.password")}</FormLabel>
            <span className="block text-xs text-muted-foreground -mt-1 mb-1">{t("auth.min_8_char")}</span>
            <FormControl>
              <div className="relative">
                <Input
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  className="bg-white border border-gray-300 p-2 rounded-md"
                  {...field}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4 text-gray-500" />
                  ) : (
                    <Eye className="h-4 w-4 text-gray-500" />
                  )}
                </Button>
              </div>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="confirmPassword"
        render={({ field }) => (
          <FormItem>
            <FormLabel className="text-[#8791F3]">{t("auth.confirm_password")}</FormLabel>
            <FormControl>
              <div className="relative">
                <Input
                  type={showConfirmPassword ? "text" : "password"}
                  placeholder="••••••••"
                  className="bg-white border border-gray-300 p-2 rounded-md"
                  {...field}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? (
                    <EyeOff className="h-4 w-4 text-gray-500" />
                  ) : (
                    <Eye className="h-4 w-4 text-gray-500" />
                  )}
                </Button>
              </div>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    </>
  );
};
